import {
  type GetVisitsRequestQuery,
  type GetVisitsWithCaseDataResponse,
} from "@clipboard-health/contract-worker-app-bff";
import { useWorkerPreferences } from "@src/appV2/Agents/api/useWorkerPreferences";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";

import { type PatientData } from "./types";
import { useGetVisitsWithCaseData } from "./useGetVisitsWithCaseData";

export function useVisitsListData(
  bookedStatus: { booked: GetVisitsRequestQuery["filter"]["booked"] },
  options?: UseGetQueryOptions<GetVisitsWithCaseDataResponse>
) {
  const worker = useDefinedWorker();
  const { filters } = useWorkerPreferences();

  const { data: workerQualifications, isSuccess: workerQualificationsAreLoaded } =
    useWorkerQualifications();

  const queryParams: GetVisitsRequestQuery["filter"] = {
    booked: bookedStatus.booked,
    longitude: worker.geoLocation?.coordinates[0] ?? 0,
    latitude: worker.geoLocation?.coordinates[1] ?? 0,
    maxDistanceInMiles: filters?.distance,
    qualifications: workerQualificationsAreLoaded
      ? workerQualifications?.map((q) => q.attributes.name).join(",")
      : undefined,
  };

  const {
    data: visitsData,
    isLoading,
    isSuccess,
  } = useGetVisitsWithCaseData(queryParams, {
    ...options,
    enabled: workerQualificationsAreLoaded,
  });

  const visits = visitsData?.data ?? [];
  const cases = visitsData?.included.filter((record) => record.type === "case") ?? [];
  const workplaces = visitsData?.included.filter((record) => record.type === "workplace") ?? [];
  const caseIdToPatientMap = new Map<string, PatientData>();
  const caseIdToSpecialtiesMap = new Map<string, string[]>();
  const caseIdToWorkplaceNameMap = new Map<string, string>();

  cases?.forEach((caseData) => {
    caseIdToPatientMap.set(caseData.id, caseData.attributes.patient);
    caseIdToSpecialtiesMap.set(caseData.id, caseData.attributes.specialties);
    caseIdToWorkplaceNameMap.set(
      caseData.id,
      workplaces.find((workplace) => {
        return workplace.id === caseData.attributes.workplaceId;
      })?.attributes.name ?? ""
    );
  });

  return {
    visits,
    isVisitsLoading: isLoading,
    isVisitsSuccess: isSuccess,
    caseIdToPatientMap,
    caseIdToSpecialtiesMap,
    caseIdToWorkplaceNameMap,
  };
}
